import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, MapPin, Users, Music, Coffee, Utensils, Flame, Star, TreePine } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ElevatedEscapePage = () => {
  usePageTitle('Elevated Escape Camping Retreat');
  const [regForm, setRegForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    attendees: '1',
    newsletter: true
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [regSubmitted, setRegSubmitted] = useState(false);
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleRegChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regForm)
      });
      
      if (response.ok) {
        setRegSubmitted(true);
        setRegForm({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          attendees: '1',
          newsletter: true
        });
      }
    } catch (error) {
      console.error('Error submitting registration:', error);
    }
    setSubmitting(false);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail, source: 'website' })
      });
      
      if (response.ok) {
        setNewsletterSubmitted(true);
        setNewsletterEmail('');
      }
    } catch (error) {
      console.error('Error subscribing:', error);
    }
    setSubmitting(false);
  };

  const features = [
    { icon: Flame, text: "Campfires & late-night connection" },
    { icon: Music, text: "Live Music & DJ" },
    { icon: Coffee, text: "Coffee & Tea Bar" },
    { icon: Utensils, text: "3 Community Meals" },
    { icon: Star, text: "Puff N Paint Experience" },
    { icon: TreePine, text: "Workshops & guided experiences" },
    { icon: Users, text: "Breathwork, nature, and reflection" },
    { icon: Star, text: "Stargazing & downtime" }
  ];

  const workshops = [
    "Cannabis 101",
    "Breathwork",
    "Foraging Workshop",
    "Puff N Paint",
    "Community Conversations"
  ];

  return (
    <div>
      {/* Hero Banner */}
      <section className="relative">
        <img
          src="/images/site/hero.jpg"
          alt="Elevated Escape Camping Retreat 2026"
          className="w-full h-[500px] object-cover"
        />
        <div className="absolute inset-0 bg-black/40 flex items-end">
          <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-12 w-full">
            <div className="flex items-center gap-4 text-white mb-4">
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Camp Waub-O-Jeeg
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                August 21-23, 2026
              </span>
            </div>
            <p className="text-white/80">Hosted by ElevateMN</p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="bg-brand-light py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-6">
              Step Away From the Noise
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              Elevated Escape is a community-driven camping retreat experience where nature, connection, and intentional living come together.
            </p>
            <p className="text-lg text-gray-700 mb-6">
              This is a weekend designed to help you unplug, reset, and reconnect. This is not just another event. This is where you come to breathe again.
            </p>
            <div className="bg-white p-6 mt-8">
              <p className="text-gray-600 italic">
                "Tents under the trees. Coffee at sunrise. Music in the air. Laughter around the fire. A full weekend to slow down and reconnect—with yourself and others."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-12">
            What to Expect
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-4">
                <feature.icon className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" />
                <p className="text-gray-700">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Workshops */}
      <section className="bg-brand-light py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-12">
            Featured Workshops
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workshops.map((workshop, index) => (
              <div key={index} className="bg-white p-6">
                <p className="text-lg font-medium text-brand-dark">{workshop}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-8">
              Who This Is For
            </h2>
            <p className="text-gray-700 mb-6">This is for people who are ready to:</p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-brand-gold"></span>
                Unplug from everyday stress
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-brand-gold"></span>
                Reconnect with nature
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-brand-gold"></span>
                Meet genuine people
              </li>
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-brand-gold"></span>
                Experience something real
              </li>
            </ul>
            <p className="text-gray-700 mt-6">
              Come solo or with friends. Either way, you will leave with community.
            </p>
          </div>
        </div>
      </section>

      {/* Event Details */}
      <section className="bg-brand-dark py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-8">
                Event Details
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-brand-gold uppercase tracking-wide mb-2">
                    Location
                  </h3>
                  <p className="text-gray-300">Camp Waub-O-Jeeg, Taylors Falls, MN</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-brand-gold uppercase tracking-wide mb-2">
                    Dates
                  </h3>
                  <p className="text-gray-300">August 21, 22 & 23, 2026</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-brand-gold uppercase tracking-wide mb-2">
                    Capacity
                  </h3>
                  <p className="text-gray-300">Limited to 300 attendees</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-brand-gold uppercase tracking-wide mb-2">
                    Important Notes
                  </h3>
                  <ul className="text-gray-300 space-y-2">
                    <li>This is a camping-based event (no cabins available)</li>
                    <li>Bring your own gear</li>
                    <li>Weekend passes available</li>
                  </ul>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-2xl lg:text-3xl font-semibold text-white mb-8">
                Tickets
              </h2>
              <p className="text-gray-300 mb-4">
                Weekend passes available. Ticket tiers and pricing coming soon.
              </p>
              <p className="text-brand-gold font-medium">
                Register your interest below to be notified when tickets drop.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="bg-brand-light py-16 lg:py-20" id="register">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Registration */}
            <div>
              <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-4">
                Register Your Interest
              </h2>
              <p className="text-gray-600 mb-8">
                Be the first to know when tickets become available.
              </p>

              {regSubmitted ? (
                <div className="bg-white p-8">
                  <p className="text-brand-dark font-medium mb-2">Thank you for registering your interest!</p>
                  <p className="text-gray-600">We will notify you when tickets become available.</p>
                  <button
                    onClick={() => setRegSubmitted(false)}
                    className="mt-6 text-brand-gold font-medium hover:underline"
                  >
                    Register another person
                  </button>
                </div>
              ) : (
                <form onSubmit={handleRegSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name" className="text-brand-dark font-medium">
                        First Name
                      </Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        type="text"
                        required
                        value={regForm.first_name}
                        onChange={handleRegChange}
                        className="mt-2 bg-white border-gray-300 text-brand-dark"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name" className="text-brand-dark font-medium">
                        Last Name
                      </Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        type="text"
                        required
                        value={regForm.last_name}
                        onChange={handleRegChange}
                        className="mt-2 bg-white border-gray-300 text-brand-dark"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="regEmail" className="text-brand-dark font-medium">
                      Email
                    </Label>
                    <Input
                      id="regEmail"
                      name="email"
                      type="email"
                      required
                      value={regForm.email}
                      onChange={handleRegChange}
                      className="mt-2 bg-white border-gray-300 text-brand-dark"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-brand-dark font-medium">
                      Phone (optional)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={regForm.phone}
                      onChange={handleRegChange}
                      className="mt-2 bg-white border-gray-300 text-brand-dark"
                    />
                  </div>

                  <div>
                    <Label htmlFor="attendees" className="text-brand-dark font-medium">
                      Expected Number of Attendees
                    </Label>
                    <select
                      id="attendees"
                      name="attendees"
                      value={regForm.attendees}
                      onChange={handleRegChange}
                      className="mt-2 w-full bg-white border border-gray-300 text-brand-dark p-2 rounded-md"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5+">5+</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="newsletter"
                      name="newsletter"
                      checked={regForm.newsletter}
                      onChange={handleRegChange}
                      className="w-4 h-4 accent-brand-gold"
                    />
                    <Label htmlFor="newsletter" className="text-gray-700">
                      Subscribe to ElevateMN newsletter for updates
                    </Label>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-brand-gold px-8 py-3 text-base font-semibold text-brand-dark transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {submitting ? 'Submitting...' : 'Register Interest'}
                  </button>
                </form>
              )}
            </div>

            {/* Newsletter Signup */}
            <div>
              <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-4">
                Join Our Newsletter
              </h2>
              <p className="text-gray-600 mb-8">
                Stay connected with ElevateMN. Get updates on events, programs, and community news.
              </p>

              {newsletterSubmitted ? (
                <div className="bg-white p-8">
                  <p className="text-brand-dark font-medium mb-2">You are subscribed!</p>
                  <p className="text-gray-600">Thank you for joining our newsletter.</p>
                  <button
                    onClick={() => setNewsletterSubmitted(false)}
                    className="mt-6 text-brand-gold font-medium hover:underline"
                  >
                    Subscribe another email
                  </button>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="newsletterEmail" className="text-brand-dark font-medium">
                      Email Address
                    </Label>
                    <Input
                      id="newsletterEmail"
                      type="email"
                      required
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      className="mt-2 bg-white border-gray-300 text-brand-dark"
                      placeholder="your@email.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-brand-dark px-8 py-3 text-base font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {submitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-gold py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-semibold text-brand-dark mb-4">
            You Do Not Need Another Weekend Out
          </h2>
          <p className="text-brand-dark opacity-80 mb-8">
            You need a weekend like this.
          </p>
          <a
            href="#register"
            className="inline-block bg-brand-dark px-8 py-3.5 text-base font-semibold text-white transition-opacity hover:opacity-90"
          >
            Register Your Interest
          </a>
        </div>
      </section>
    </div>
  );
};

export default ElevatedEscapePage;
